import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { format, parseISO, subDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { FileText, Loader2, Calendar, User, MapPin, Monitor, Clock, Building2, Filter, Image as ImageIcon, Search, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

// Helper to format time in EST
const formatTimeEST = (isoString) => {
  const date = parseISO(isoString);
  const estDate = toZonedTime(date, "America/New_York");
  return format(estDate, "h:mm a") + " EST";
};

// Component to display attachments
const AttachmentList = ({ attachments }) => {
  if (!attachments || attachments.length === 0) return null;
  
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((att) => (
        <a
          key={att.id}
          href={`${BASE_URL}${att.url}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded hover:bg-blue-100 transition-colors"
        >
          <ImageIcon className="h-3 w-3" />
          {att.original_name || "Image"}
        </a>
      ))}
    </div>
  );
};

export default function AllLogsPage() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Default to Today instead of Yesterday
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");

  // Get last 14 days for date selection
  const getDateOptions = () => {
    const dates = [];
    for (let i = 0; i <= 14; i++) {
      const date = subDays(new Date(), i);
      dates.push({
        value: format(date, "yyyy-MM-dd"),
        label: i === 0 ? `Today (${format(date, "MMM d")})` : i === 1 ? `Yesterday (${format(date, "MMM d")})` : format(date, "EEEE, MMM d"),
      });
    }
    return dates;
  };

  const dateOptions = getDateOptions();

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [selectedDate, selectedTeam, selectedUser]);

  const fetchInitialData = async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`),
      ]);
      // Include all active users regardless of role, sorted alphabetically
      const activeUsers = usersRes.data.filter((u) => u.is_active);
      activeUsers.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setUsers(activeUsers);
      setTeams(teamsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("from_date", selectedDate);
      params.append("to_date", selectedDate);
      if (selectedTeam !== "all") {
        params.append("team_id", selectedTeam);
      }
      if (selectedUser !== "all") {
        params.append("user_id", selectedUser);
      }

      const response = await axios.get(`${API}/admin/logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  // Filter users by selected team
  const filteredUsers = selectedTeam === "all" 
    ? users 
    : users.filter((u) => u.team_ids && u.team_ids.includes(selectedTeam));

  // Calculate stats - account for selected user filter
  // Exclude exempt users from scope for missing logs calculation
  const usersInScope = selectedUser !== "all" 
    ? users.filter((u) => u.id === selectedUser)
    : filteredUsers;
  const nonExemptUsersInScope = usersInScope.filter((u) => !u.is_exempt);
  const totalUsersInScope = nonExemptUsersInScope.length;
  const submittedCount = logs.length;
  const uniqueUserIds = new Set(logs.map((l) => l.user_id));
  const missingCount = totalUsersInScope - uniqueUserIds.size;

  // Get users who haven't submitted (exclude exempt users)
  const missingUsers = nonExemptUsersInScope.filter((u) => !uniqueUserIds.has(u.id));

  // Apply search filter to logs and missing users
  const searchLower = searchQuery.toLowerCase().trim();
  const filteredLogs = searchQuery 
    ? logs.filter((log) => 
        log.user_name?.toLowerCase().includes(searchLower) || 
        log.user_email?.toLowerCase().includes(searchLower)
      )
    : logs;
  const filteredMissingUsers = searchQuery
    ? missingUsers.filter((u) => 
        u.full_name?.toLowerCase().includes(searchLower) || 
        u.email?.toLowerCase().includes(searchLower)
      )
    : missingUsers;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          All Logs
        </h1>
        <p className="text-slate-600 mt-1">
          View and filter logs across all teams and users
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="w-[220px]">
              <Select value={selectedDate} onValueChange={setSelectedDate}>
                <SelectTrigger data-testid="all-logs-date-selector">
                  <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Select date" />
                </SelectTrigger>
                <SelectContent>
                  {dateOptions.map((date) => (
                    <SelectItem key={date.value} value={date.value}>
                      {date.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Select value={selectedTeam} onValueChange={(val) => { setSelectedTeam(val); setSelectedUser("all"); }}>
                <SelectTrigger data-testid="all-logs-team-selector">
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger data-testid="all-logs-user-selector">
                  <User className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {filteredUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Search by Name */}
            <div className="w-[280px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8"
                data-testid="all-logs-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalUsersInScope}</p>
                <p className="text-sm text-slate-500">Users in Scope</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{submittedCount}</p>
                <p className="text-sm text-slate-500">Logs Submitted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{missingCount}</p>
                <p className="text-sm text-slate-500">Missing Logs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Missing Users */}
          {filteredMissingUsers.length > 0 && selectedUser === "all" && (
            <Card className="border-red-100">
              <CardHeader>
                <CardTitle className="text-red-700">
                  Missing Logs
                  {searchQuery && <span className="text-sm font-normal ml-2">({filteredMissingUsers.length} matching "{searchQuery}")</span>}
                </CardTitle>
                <CardDescription>Users who haven't submitted their log for {format(parseISO(selectedDate), "MMMM d, yyyy")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {filteredMissingUsers.map((user) => (
                    <Badge key={user.id} variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      {user.full_name} {user.team_names && user.team_names.length > 0 && `(${user.team_names.join(", ")})`}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submitted Logs */}
          {filteredLogs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No logs found</h3>
                <p className="text-slate-500 mt-1">{searchQuery ? `No logs match "${searchQuery}"` : "No logs match your filter criteria"}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {searchQuery && (
                <p className="text-sm text-slate-500">Showing {filteredLogs.length} log{filteredLogs.length !== 1 ? 's' : ''} matching "{searchQuery}"</p>
              )}
              {filteredLogs.map((log) => (
                <Card key={log.id} data-testid={`log-detail-${log.id}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                          {log.user_name}
                          {log.team_names && log.team_names.length > 0 && (
                            <div className="flex gap-1">
                              {log.team_names.map((name, idx) => (
                                <Badge key={idx} variant="outline" className="font-normal text-xs">
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </CardTitle>
                        <CardDescription>{log.user_email}</CardDescription>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Submitted
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Log Content */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                          Tasks Worked On Today
                        </h4>
                        <div 
                          className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: log.tasks_today }}
                        />
                        <AttachmentList attachments={log.tasks_today_attachments} />
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                          Challenges and Hurdles
                        </h4>
                        <div 
                          className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: log.challenges }}
                        />
                        <AttachmentList attachments={log.challenges_attachments} />
                      </div>
                      <div>
                        <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                          Plan for Tomorrow
                        </h4>
                        <div 
                          className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: log.plan_tomorrow }}
                        />
                        <AttachmentList attachments={log.plan_tomorrow_attachments} />
                      </div>
                      {log.ai_tools_used && (
                        <div>
                          <h4 className="text-xs font-medium text-purple-600 uppercase tracking-wider mb-2">
                            AI Tools Discovery
                          </h4>
                          <div 
                            className="text-sm text-slate-700 bg-purple-50 p-3 rounded-lg prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: log.ai_tools_used }}
                          />
                          <AttachmentList attachments={log.ai_tools_attachments} />
                        </div>
                      )}
                    </div>

                    {/* Metadata */}
                    <div className="pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-slate-500">
                          <Clock className="h-4 w-4" />
                          <span>
                            {formatTimeEST(log.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-500">
                          <Monitor className="h-4 w-4" />
                          <span>{log.browser_name} / {log.os_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-500">
                          <MapPin className="h-4 w-4" />
                          <span>
                            {[log.geo_city, log.geo_region, log.geo_country].filter(Boolean).join(", ") || log.ip_address}
                          </span>
                        </div>
                        <div className="text-slate-400 text-xs">
                          IP: {log.ip_address}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
